import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Button } from './ui/Button';
import { LogOut, User as UserIcon, ChevronDown } from 'lucide-react';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [hasTeam, setHasTeam] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (user) {
            apiService.hasTeamMembers().then(setHasTeam);
        }
    }, [user]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setUserMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    if (!user) {
        return <div className="min-h-screen bg-gray-50">{children}</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 justify-between items-center">
                        <div className="flex items-center space-x-8">
                            <div className="flex items-center space-x-2">
                                <div className="bg-blue-600 rounded-lg p-2">
                                    <img src="/logo.png" alt="Skill Management Logo" className="h-6 w-auto" />
                                </div>
                                <span className="text-xl font-bold text-gray-900">Skill Management</span>
                            </div>
                            <div className="hidden sm:flex sm:space-x-1">
                                <Link
                                    to="/engineer/dashboard"
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname.includes('/engineer')
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    My Submissions
                                </Link>
                                {hasTeam && (
                                    <Link
                                        to="/manager/dashboard"
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname.includes('/manager')
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                            }`}
                                    >
                                        Team Reviews
                                    </Link>
                                )}
                                <Link
                                    to="/reports"
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/reports'
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    Reports
                                </Link>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="relative" ref={menuRef}>
                                <button
                                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                                    className="bg-gray-100 px-3 py-1.5 rounded-md flex items-center space-x-2 text-sm border border-gray-200 hover:bg-gray-200 transition-colors"
                                >
                                    <UserIcon className="h-4 w-4 text-gray-600" />
                                    <span className="text-gray-700 font-medium">{user.name}</span>
                                    <ChevronDown className={`h-4 w-4 text-gray-600 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {/* Dropdown Menu */}
                                {userMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                                        <Link
                                            to="/change-password"
                                            onClick={() => setUserMenuOpen(false)}
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                                        >
                                            Change Password
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <Button variant="ghost" size="sm" onClick={handleLogout} className="hover:bg-red-50 hover:text-red-600">
                                <LogOut className="mr-2 h-4 w-4" />
                                Logout
                            </Button>
                        </div>
                    </div>
                </div>
            </nav>
            <main className="py-8">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
            </main>
        </div>
    );
};
